import { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import useActiveCategory from '../hooks/useActiveCategory';
import useSearch from '../hooks/useSearch';

const Navbar = () => {
  const location = useLocation();

  const [searchModalOpen, setSearchModalOpen] = useState(false);

  const { searchQuery, setSearchQuery, handleSearch, handleKeyPress } =
    useSearch();

  // Mover menuData al principio usando useMemo para evitar recreación en cada render
  const menuData = useMemo(
    () => [
      {
        name: 'INICIO',
        path: 'inicio',
        slug: 'inicio',
        url: '/',
      },
      {
        name: 'EMPAQUE Y LOGÍSTICA',
        path: 'empaque-logistica',
        slug: 'empaque-logistica',
        url: '/category/empaque-y-logistica',
        subcategories: [
          {
            name: 'Almacenamiento, Manejo de Cargas y Seguridad Logística',
            path: 'almacenamiento',
            slug: 'almacenamiento-manejo-de-cargas-y-seguridad-logistica',
            url: '/category/almacenamiento-manejo-de-cargas-y-seguridad-logistica',
          },
          {
            name: 'Cajas, Protección y Embalaje de Cartón',
            path: 'cajas',
            slug: 'cajas-proteccion-y-embalaje-de-carton',
            url: '/category/cajas-proteccion-y-embalaje-de-carton',
          },
          {
            name: 'Cintas y Adhesivos',
            path: 'cintas',
            slug: 'cintas-y-adhesivos',
            url: '/category/cintas-y-adhesivos',
          },
          {
            name: 'Embalaje y Unitización de Cargas',
            path: 'embalaje',
            slug: 'embalaje-y-unitizacion-de-cargas',
            url: '/category/embalaje-y-unitizacion-de-cargas',
            subsubcategories: [
              {
                name: 'Fleje',
                path: 'fleje',
                slug: 'fleje',
                url: '/category/fleje',
              },
              {
                name: 'Película Stretch',
                path: 'stretch',
                slug: 'pelicula-stretch',
                url: '/category/pelicula-stretch',
              },
              {
                name: 'Película Termoencogible',
                path: 'termoencogible',
                slug: 'pelicula-termoencogible',
                url: '/category/pelicula-termoencogible',
              },
              {
                name: 'Rafia',
                path: 'rafia',
                slug: 'rafia',
                url: '/category/rafia',
              },
              {
                name: 'Stretch Hood',
                path: 'stretch-hood',
                slug: 'stretch-hood',
                url: '/category/stretch-hood',
              },
            ],
          },
          {
            name: 'Empaque Protector, Relleno y Comercio Electrónico',
            path: 'empaque-protector',
            slug: 'empaque-protector-relleno-y-comercio-electronico',
            url: '/category/empaque-protector-relleno-y-comercio-electronico',
          },
          {
            name: 'Maquinaria',
            path: 'maquinaria',
            slug: 'maquinaria',
            url: '/category/maquinaria',
          },
        ],
      },
      {
        name: 'SUMINISTROS INSTITUCIONALES',
        path: 'suministros',
        slug: 'suministros-institucionales',
        url: '/category/suministros-institucionales',
        subcategories: [
          {
            name: 'Equipo de Protección Personal',
            path: 'proteccion',
            slug: 'equipo-de-proteccion-personal',
            url: '/category/equipo-de-proteccion-personal',
          },
          {
            name: 'Higiene y Limpieza',
            path: 'higiene',
            slug: 'higiene-y-limpieza',
            url: '/category/higiene-y-limpieza',
          },
          {
            name: 'Soluciones para Food Service',
            path: 'food-service',
            slug: 'soluciones-para-food-service',
            url: '/category/soluciones-para-food-service',
          },
        ],
      },
      {
        name: 'GLOSARIO Y FORMACIÓN',
        path: 'glosario',
        slug: 'glosario-y-formacion',
        url: '/category/glosario-y-formacion',
        subcategories: [
          {
            name: 'Definiciones',
            path: 'definiciones',
            slug: 'definiciones',
            url: '/category/definiciones',
          },
          {
            name: 'Historia',
            path: 'historia',
            slug: 'historia',
            url: '/category/historia',
          },
          {
            name: 'Materiales',
            path: 'materiales',
            slug: 'materiales',
            url: '/category/materiales',
          },
        ],
      },
      {
        name: 'TIENDA PACKSYS',
        path: 'tienda',
        url: 'https://packsys.com/',
      },
    ],
    []
  );

  // Estado para controlar qué elementos del menú están activos
  const { activeMenu, setActiveMenu } = useActiveCategory(menuData);
  // Estado para controlar qué menú está abierto en móviles
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Cargar estado activo desde localStorage al iniciar
  useEffect(() => {
    const loadActiveMenu = () => {
      try {
        const savedActiveMenu = localStorage.getItem('activeMenu');
        if (
          savedActiveMenu &&
          savedActiveMenu !== 'null' &&
          savedActiveMenu !== '{}'
        ) {
          const parsedMenu = JSON.parse(savedActiveMenu);
          // Verificar que el parsedMenu tenga la estructura correcta
          if (parsedMenu && typeof parsedMenu === 'object') {
            setActiveMenu({
              category: parsedMenu.category || '',
              subcategory: parsedMenu.subcategory || '',
              subsubcategory: parsedMenu.subsubcategory || '',
            });
          }
        }
      } catch (error) {
        console.error('Error parsing saved menu state:', error);
        setActiveMenu({
          category: '',
          subcategory: '',
          subsubcategory: '',
        });
      }
    };

    loadActiveMenu();
  }, [setActiveMenu]); // Agregar setActiveMenu como dependencia

  // Función para manejar clics en elementos del menú
  const handleMenuClick = (category, subcategory = '', subsubcategory = '') => {
    const newActiveMenu = {
      category: category || '',
      subcategory: subcategory || '',
      subsubcategory: subsubcategory || '',
    };

    // Solo guardar en localStorage y estado
    localStorage.setItem('activeMenu', JSON.stringify(newActiveMenu));
    setActiveMenu(newActiveMenu);
  };

  const navigateTo = (url, category, subcategory = '', subsubcategory = '') => {
    handleMenuClick(category, subcategory, subsubcategory);

    setTimeout(() => {
      if (
        window.location.pathname !==
        new URL(url, window.location.origin).pathname
      ) {
        window.location.href = url;
      }
    }, 100);
  };

  useEffect(() => {
    setSearchModalOpen(false);
  }, [location]);

  return (
    <nav className="bg-white shadow-md sticky top-0 z-100 h-24">
      <div className="w-full mx-auto px-4 sm:px-8 lg:px-12">
        <div className="flex justify-between h-24">
          {/* Logo */}
          <div className="flex items-center">
            <a href="/">
              <img
                src="https://blog.packsys.com/wp-content/uploads/2012/01/logo.png"
                alt="Packsys Logo"
                className="h-15 w-auto"
                style={{ height: '60px' }}
              />
            </a>
          </div>

          {/* Menú para escritorio */}
          <div className="hidden lg:flex items-center space-x-8">
            {menuData.map((item) => (
              <div key={item.path} className="relative group">
                <a
                  href={item.url}
                  onClick={(e) => {
                    e.preventDefault();
                    const currentPath = window.location.pathname;
                    new URL(item.url, window.location.origin);
                    const targetPath = new URL(item.url, window.location.origin)
                      .pathname;

                    // Si es una URL externa, navegar inmediatamente
                    if (
                      item.url.startsWith('http') &&
                      !item.url.includes(window.location.origin)
                    ) {
                      handleMenuClick(item.path);
                      setTimeout(() => {
                        window.open(item.url, '_blank');
                      }, 50);
                    } else if (currentPath === targetPath) {
                      handleMenuClick(item.path);
                    } else {
                      navigateTo(item.url, item.path);
                    }
                  }}
                  className={`px-3 py-2 h-24 rounded-md text-sm font-medium transition-colors duration-200 flex items-center ${
                    activeMenu.category === item.path
                      ? 'text-[#5cba3cff] border-y-2 border-[#5cba3cff]'
                      : 'text-gray-700 hover:text-[#47bb21] hover:border-y-2 hover:border-[#47bb21]'
                  }`}
                >
                  {item.name}
                  {item.subcategories && (
                    <span className="ml-2 text-xs">▼</span>
                  )}
                </a>

                {/* Mega menú desplegable */}
                {item.subcategories && (
                  <div className="absolute left-0 mt-0 w-80 bg-white shadow-lg rounded-md opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-10 border border-gray-200">
                    <div className="py-2">
                      {item.subcategories.map((subitem) => (
                        <div key={subitem.path} className="relative">
                          <a
                            href={subitem.url}
                            onClick={(e) => {
                              e.preventDefault();
                              const currentPath = window.location.pathname;
                              const targetPath = new URL(
                                subitem.url,
                                window.location.origin
                              ).pathname;

                              if (currentPath === targetPath) {
                                handleMenuClick(item.path, subitem.path);
                              } else {
                                navigateTo(
                                  subitem.url,
                                  item.path,
                                  subitem.path
                                );
                              }
                            }}
                            className={`flex justify-between items-center w-full text-left px-4 py-2 text-sm transition-colors duration-200 ${
                              activeMenu.subcategory === subitem.path
                                ? 'text-[#5cba3cff] bg-gray-50'
                                : 'text-gray-700 hover:text-[#47bb21] hover:bg-gray-50'
                            }`}
                          >
                            {subitem.name}
                            {subitem.subsubcategories && (
                              <span className="text-xs ml-2">▶</span>
                            )}
                          </a>

                          {/* Dentro del mega menú desplegable - modificar la estructura del subsubmenú */}
                          {subitem.subsubcategories && (
                            <div className="absolute left-full top-0 w-64 bg-white shadow-lg rounded-md ml-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-20 border border-gray-200">
                              <div className="py-2">
                                {subitem.subsubcategories.map((subsubitem) => (
                                  <a
                                    key={subsubitem.path}
                                    href={subsubitem.url}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      const currentPath =
                                        window.location.pathname;
                                      const targetPath = new URL(
                                        subsubitem.url,
                                        window.location.origin
                                      ).pathname;

                                      if (currentPath === targetPath) {
                                        handleMenuClick(
                                          item.path,
                                          subitem.path,
                                          subsubitem.path
                                        );
                                      } else {
                                        navigateTo(
                                          subsubitem.url,
                                          item.path,
                                          subitem.path,
                                          subsubitem.path
                                        );
                                      }
                                    }}
                                    className={`block px-4 py-2 text-sm transition-colors duration-200 ${
                                      activeMenu.subsubcategory ===
                                      subsubitem.path
                                        ? 'text-[#5cba3cff] bg-gray-50'
                                        : 'text-gray-700 hover:text-[#47bb21] hover:bg-gray-50'
                                    }`}
                                  >
                                    {subsubitem.name}
                                  </a>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
          {/* Ícono de búsqueda - MODIFICADO */}
          <div className="hidden lg:flex items-center">
            <button
              onClick={() => setSearchModalOpen(true)}
              className="text-gray-700 hover:text-[#47bb21] transition-colors duration-200 p-2"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </button>
          </div>

          {/* Menú móvil */}
          <div className="lg:hidden flex items-center">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-gray-700 hover:text-[#47bb21] transition-colors duration-200 p-2"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Modal de búsqueda para desktop */}
      {searchModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-1000 flex items-start justify-center pt-20">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4">
            <div className="p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Buscar artículos
                </h3>
                <button
                  onClick={() => setSearchModalOpen(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleSearch} className="flex gap-2">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Escribe lo que buscas..."
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  autoFocus
                />
                <button
                  type="submit"
                  className="px-6 py-2 bg-[#5cba3cff] text-white rounded-lg hover:bg-[#47bb21] transition-colors duration-200"
                >
                  Buscar
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Menú móvil desplegable - AÑADIR BÚSQUEDA */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-200">
          <div className="px-2 pt-2 pb-3 space-y-1">
            {menuData.map((item) => (
              <div key={item.path}>
                {/* Elementos sin subcategorías */}
                {!item.subcategories ? (
                  <a
                    href={item.url}
                    onClick={(e) => {
                      e.preventDefault();
                      navigateTo(item.url, item.path);
                      setMobileMenuOpen(false);
                    }}
                    className={`block px-3 py-2 rounded-md text-base font-medium transition-colors duration-200 ${
                      activeMenu.category === item.path
                        ? 'text-[#5cba3cff] bg-gray-100'
                        : 'text-gray-700 hover:text-[#47bb21] hover:bg-gray-50'
                    }`}
                  >
                    {item.name}
                  </a>
                ) : (
                  /* Elementos con subcategorías */
                  <div>
                    <button
                      onClick={() => {
                        if (activeMenu.category === item.path) {
                          handleMenuClick('');
                        } else {
                          handleMenuClick(item.path);
                        }
                      }}
                      className={`flex justify-between items-center w-full text-left px-3 py-2 rounded-md text-base font-medium transition-colors duration-200 ${
                        activeMenu.category === item.path
                          ? 'text-[#5cba3cff] bg-gray-100'
                          : 'text-gray-700 hover:text-[#47bb21] hover:bg-gray-50'
                      }`}
                    >
                      {item.name}
                      <span
                        className={`transform transition-transform ${
                          activeMenu.category === item.path ? 'rotate-180' : ''
                        }`}
                      >
                        ▼
                      </span>
                    </button>

                    {/* Submenús en móvil */}
                    {activeMenu.category === item.path && (
                      <div className="ml-4 mt-1 space-y-1">
                        {item.subcategories.map((subitem) => (
                          <div key={subitem.path}>
                            {/* Subitems sin subsubcategorías */}
                            {!subitem.subsubcategories ? (
                              <a
                                href={subitem.url}
                                onClick={(e) => {
                                  e.preventDefault();
                                  navigateTo(
                                    subitem.url,
                                    item.path,
                                    subitem.path
                                  );
                                  setMobileMenuOpen(false);
                                }}
                                className={`block px-3 py-2 rounded-md text-sm transition-colors duration-200 ${
                                  activeMenu.subcategory === subitem.path
                                    ? 'text-[#c2eb8d] bg-gray-100'
                                    : 'text-gray-600 hover:text-[#47bb21] hover:bg-gray-50'
                                }`}
                              >
                                {subitem.name}
                              </a>
                            ) : (
                              /* Subitems con subsubcategorías */
                              <div>
                                <button
                                  onClick={() => {
                                    if (
                                      activeMenu.subcategory === subitem.path
                                    ) {
                                      handleMenuClick(item.path, '');
                                    } else {
                                      handleMenuClick(item.path, subitem.path);
                                    }
                                  }}
                                  className={`flex justify-between items-center w-full text-left px-3 py-2 rounded-md text-sm transition-colors duration-200 ${
                                    activeMenu.subcategory === subitem.path
                                      ? 'text-[#c2eb8d] bg-gray-100'
                                      : 'text-gray-600 hover:text-[#47bb21] hover:bg-gray-50'
                                  }`}
                                >
                                  {subitem.name}
                                  <span
                                    className={`transform transition-transform ${
                                      activeMenu.subcategory === subitem.path
                                        ? 'rotate-180'
                                        : ''
                                    }`}
                                  >
                                    ▼
                                  </span>
                                </button>

                                {/* Subsubmenús en móvil */}
                                {activeMenu.subcategory === subitem.path && (
                                  <div className="ml-4 mt-1 space-y-1">
                                    {subitem.subsubcategories.map(
                                      (subsubitem) => (
                                        <a
                                          key={subsubitem.path}
                                          href={subsubitem.url}
                                          onClick={(e) => {
                                            e.preventDefault();
                                            navigateTo(
                                              subsubitem.url,
                                              item.path,
                                              subitem.path,
                                              subsubitem.path
                                            );
                                            setMobileMenuOpen(false);
                                          }}
                                          className={`block px-3 py-2 rounded-md text-xs transition-colors duration-200 ${
                                            activeMenu.subsubcategory ===
                                            subsubitem.path
                                              ? 'text-[#c2eb8d] bg-gray-100'
                                              : 'text-gray-500 hover:text-[#47bb21] hover:bg-gray-50'
                                          }`}
                                        >
                                          {subsubitem.name}
                                        </a>
                                      )
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}

            {/* Barra de búsqueda en móvil */}
            <div className="px-3 py-2">
              <form onSubmit={handleSearch} className="flex gap-2">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => {
                    handleKeyPress(e);
                    if (e.key === 'Enter') {
                      setMobileMenuOpen(false);
                    }
                  }}
                  placeholder="Buscar artículos..."
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-green-500"
                />
                <button
                  type="submit"
                  onClick={() => setMobileMenuOpen(false)}
                  className="px-4 py-2 bg-[#5cba3cff] text-white rounded-md text-sm hover:bg-[#47bb21]"
                >
                  🔍
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
